import type { Option } from "@tento-lona/sheets";
import type { SettleResult } from "@tento-scroll/engine/scroll-engine";

/** One balanced observer suspension. Its owner releases after installing the
 * new view; disconnect may cancel even before that installation finishes. */
export class ObserverSettle {
  readonly #controller = new AbortController();
  #resumed = false;
  #released = false;
  #timer: Option<number> = null;

  readonly #host: ObserverSettle.Host;
  constructor(host: ObserverSettle.Host) {
    this.#host = host;
    host.suspend();
  }

  release(): void {
    if (this.#released || this.#resumed) return;
    this.#released = true;
    this.#timer = this.#host.setTimeout(() => this.cancel(), 2500);
    this.#quiesce();
  }

  cancel(): void {
    if (this.#resumed) return;
    this.#resumed = true;
    this.#controller.abort();
    if (this.#timer != null) this.#host.clearTimeout(this.#timer);
    this.#timer = null;
    this.#host.resume();
    this.#host.finished();
  }

  #quiesce(): void {
    if (this.#resumed) return;
    void this.#host.wait(this.#controller.signal).then((result) => {
      if (this.#resumed) return;
      if (result.kind === "cancelled") {
        this.cancel();
        return;
      }
      if (this.#host.pending()) {
        this.#quiesce();
        return;
      }
      this.#host.afterMutate(() => {
        if (this.#resumed) return;
        if (this.#host.pending()) {
          this.#quiesce();
          return;
        }
        this.cancel();
      });
    });
  }
}

export namespace ObserverSettle {
  export interface Host {
    suspend(): void;
    resume(): void;
    wait(signal: AbortSignal): Promise<SettleResult>;
    pending(): boolean;
    afterMutate(callback: () => void): void;
    setTimeout(callback: () => void, milliseconds: number): number;
    clearTimeout(id: number): void;
    finished(): void;
  }
}
